const DELIMITERS = [',', ';', '\t', '|'];

/**
 * The separator a CSV uses: whichever of , ; tab | appears most in its
 * first line outside quotes (so a comma inside "New York, NY" does not
 * vote). Falls back to a comma.
 */
export function detectDelimiter(text) {
  const counts = Object.fromEntries(DELIMITERS.map((delimiter) => [delimiter, 0]));
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (!inQuotes) {
      if (char === '\n' || char === '\r') {
        // Only the first non-empty line decides.
        if (Object.values(counts).some((count) => count > 0)) break;
      } else if (char in counts) {
        counts[char] += 1;
      }
    }
  }

  const best = DELIMITERS.reduce((a, b) => (counts[b] > counts[a] ? b : a), ',');
  return counts[best] > 0 ? best : ',';
}

/**
 * Parses CSV text (RFC 4180: quoted fields, "" as an escaped quote,
 * commas and line breaks inside quotes, CRLF or LF) into an array of
 * rows, each an array of strings. Blank lines are skipped.
 *
 * `maxRows` stops early - a huge file is never parsed past what will be
 * shown - and `truncated` says whether rows were left unread.
 */
export default function parseCsv(text, { maxRows = Infinity } = {}) {
  const source = (text || '').replace(/^﻿/, '');
  const delimiter = detectDelimiter(source);

  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let truncated = false;

  const endRow = () => {
    row.push(field);
    field = '';
    // A line with nothing on it is not a row.
    if (row.some((cell) => cell !== '')) rows.push(row);
    row = [];
  };

  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];

    if (inQuotes) {
      if (char === '"') {
        if (source[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
    } else if (char === '"' && field === '') {
      inQuotes = true;
    } else if (char === delimiter) {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && source[i + 1] === '\n') i += 1;
      endRow();

      if (rows.length >= maxRows) {
        // Anything else left after the last kept row means it was cut.
        truncated = source.slice(i + 1).trim() !== '';
        return { rows, truncated };
      }
    } else {
      field += char;
    }
  }

  if (field !== '' || row.length > 0) endRow();

  if (rows.length > maxRows) {
    rows.length = maxRows;
    truncated = true;
  }

  return { rows, truncated };
}
